import React, {FC, useState, useEffect} from 'react'

import ClickUpOAuthModal from './ClickUpOAuthModal'
import {clickupApiService, ClickUpIntegrationStatus} from '../services/clickupApi'
import clickupLogo from './clickup-logo.png'

interface ClickupIntegrationCardProps {}

const ClickupIntegrationCard: FC<ClickupIntegrationCardProps> = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)
  const [showOAuthModal, setShowOAuthModal] = useState(false)
  const [integrationStatus, setIntegrationStatus] = useState<ClickUpIntegrationStatus | null>(null)

  // ✅ CARREGA STATUS AUTOMATICAMENTE: Quando o componente é montado
  useEffect(() => {
    // Usa a mesma chave de autenticação do sistema
    const authKey = 'impersonating-auth-token';
    const authData = localStorage.getItem(authKey);
    
    let token = null;
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        token = auth.api_token;
      } catch (error) {
        console.error('Erro ao parsear dados de autenticação:', error);
      }
    }
    
    // Fallback para outras chaves
    if (!token) {
      token = localStorage.getItem('access_token') || 
              localStorage.getItem('token') || 
              localStorage.getItem('auth_token');
    }
    
    if (token) {
      loadIntegrationStatus()
    }
  }, [])

  const loadIntegrationStatus = async () => {
    try {
      console.log('🔍 [ClickUp Card] Carregando status da integração...')
      setIsLoading(true)
      const status = await clickupApiService.getStatus()
      console.log('🔍 [ClickUp Card] Status recebido:', status)
      setIntegrationStatus(status)
      setIsConnected(status.is_connected)
      console.log('🔍 [ClickUp Card] isConnected definido como:', status.is_connected)
    } catch (error: any) {
      console.error('❌ [ClickUp Card] Erro ao carregar status:', error)
      setIsConnected(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnect = () => {
    setShowOAuthModal(true)
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      await clickupApiService.disconnect()
      setIsConnected(false)
      setMessage({type: 'success', text: 'ClickUp desconectado com sucesso!'})
    } catch (error: any) {
      console.error('❌ [ClickUp Card] Erro ao desconectar:', error)
      setMessage({type: 'error', text: 'Erro ao desconectar do ClickUp'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className='card h-100 w-100 d-flex flex-column'>
        <div className='card-header border-0 pt-5'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-50px me-5'>
              <div className='symbol-label bg-light-success d-flex align-items-center justify-content-center'>
                <img 
                  src={clickupLogo} 
                  alt='ClickUp Logo' 
                  style={{width: '32px', height: '32px', objectFit: 'contain'}}
                />
              </div>
            </div>
            <div className='d-flex flex-column'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4 mb-2'>ClickUp</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Plataforma de produtividade
                </span>
              </h3>
            </div>
          </div>
        </div>
        
        <div className='card-body pt-0 d-flex flex-column flex-grow-1'>
          <div className='mb-6 flex-grow-1'>
            <p className='text-muted fs-7 mb-4'>
              Importe workspaces, spaces e tarefas do ClickUp para o TaskRush. 
              Mantenha seus projetos sincronizados entre as plataformas.
            </p>
            
            {/* Mensagens de sucesso/erro */}
            {message && (
              <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-4`} role='alert'>
                {message.text}
                <button type='button' className='btn-close' onClick={() => setMessage(null)}></button>
              </div>
            )}
            
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <span className='text-muted fs-7'>Status:</span>
                <span className={`badge badge-light-${isConnected ? 'success' : 'warning'} fs-8 px-3 py-2`}>
                  {isConnected ? 'Conectado' : 'Desconectado'}
                </span>
              </div>
              
              {/* Informação do usuário ClickUp */}
              {isConnected && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Usuário ClickUp:</span>
                  <span className='text-muted fs-7'>@{integrationStatus?.integration?.clickup_username || 'N/A'}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className='mt-auto'>
            <div className='d-flex flex-column gap-2'>
              {!isConnected ? (
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleConnect}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Conectando...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-plug me-2'></i>
                      Conectar com ClickUp
                    </>
                  )}
                </button>
              ) : (
                <button
                  className='btn btn-sm btn-light-danger w-100'
                  onClick={handleDisconnect}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Desconectando...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-unlink me-2'></i>
                      Desconectar do ClickUp
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <ClickUpOAuthModal
        show={showOAuthModal}
        onClose={() => setShowOAuthModal(false)}
        onSuccess={(integration) => {
          setMessage({type: 'success', text: 'ClickUp conectado com sucesso!'})
          loadIntegrationStatus()
        }}
      />
      
    </>
  )
}

export default ClickupIntegrationCard
